import { DocumentType, ArticleType, ModuleType, Tag, Article } from '@dap/sanity/types';
import { groqQuery } from '@shared/utils';
import { SanityReference } from 'sanity-codegen';
import { tagsQuery } from './tagQueries';

export interface GetNewsArticlesPreviewQueryProps {
  brandKey: string;
  brandName: string;
  limit?: number;
  tags: SanityReference<Tag>[];
  articles: SanityReference<Article>[];
  subject?: string;
}

export const getNewsArticlePreviewsQuery = ({
  brandKey,
  limit,
  tags,
  articles,
  subject,
}: Omit<GetNewsArticlesPreviewQueryProps, 'brandName'>) => {
  // Select articles that have at least one of the selected tags
  const tagsFilter =
    tags && tags.length > 0
      ? `count((articleTags[]->_id)[@ in [${tags.map((r) => `'${r._ref}'`).join(',')}]]) > 0`
      : undefined;

  // Select specific articles
  const articleFilter =
    articles && articles.length > 0
      ? `_id in [${articles.map((r) => `'${r._ref}'`).join(',')}]`
      : undefined;

  // Filters to use when querying for brand articles
  const brandFilters = [
    `_type == "${DocumentType.Article}"`,
    `"${brandKey}" in brand`,
    `module == "${ModuleType.newsModule}"`,
    `(defined(publishAt) && dateTime(publishAt) <= dateTime(now()))`,
    articleFilter || tagsFilter,
  ];

  // Filters to use when querying for subject articles
  const subjectFilters = [
    `_type == "${DocumentType.SubjectArticle}"`,
    `subject->slug.current == "${subject}"`,
    `((defined(brands) && "${brandKey}" in brands) || !defined(brands))`,
    `((defined(publishAt) && dateTime(publishAt) <= dateTime(now())) || !defined(publishAt))`,
    articleFilter || tagsFilter,
  ];

  const filters = subject ? subjectFilters : brandFilters;

  const query = groqQuery({
    filters: filters,
    // Ordering will be ignored if predefined articles are used
    ordering: [{ fieldname: 'publishAt', order: 'desc' }],
    projections: [
      '"id": _id',
      '"url": url.current',
      'intro',
      '"image": image.asset->',
      'title',
      'author[]->',
      'publishAt',
      `articleTags[]->{
        ${tagsQuery}
      }`,
      `subject->{title, "slug": slug.current}`,
      'tags',
    ],
    // Only limit the articles if no predefined articles are used
    slice: limit && !articleFilter ? { start: 0, end: limit } : undefined,
  });

  return query;
};
